import { type Application } from 'pixi.js';
import { type System, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import type {
	AmmoComponent,
	CameraResource,
	DebugControlsResource,
	EnemyComponent,
	PlatformComponent,
	ProjectileComponent,
	StageBounds,
	TimeScaleResource,
	TransformComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';
import { clamp, lerp, randomInRange } from '@game/scenes/characterPrototype/math';
import { moveTowards } from './moveTowards';
import { hasLineOfSight, findLineOfSightX } from './los';

export type SpawnProjectileFn = (
	world: Parameters<System>[0]['world'],
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	projectiles: ComponentStore<ProjectileComponent>,
	graphics: ComponentStore<import('pixi.js').Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	vx: number,
	vy: number,
	options?: {
		fromEnemy?: boolean;
		lifetime?: number;
		size?: number;
		fill?: number;
		stroke?: number;
	}
) => void;

export type EnemySystemOptions = {
	boundsKey: string;
	debugControlsKey: string;
	ammoClipSize: number;
	spawnProjectile: SpawnProjectileFn;
};

export function createEnemySystem(
	enemies: ComponentStore<EnemyComponent>,
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	projectiles: ComponentStore<ProjectileComponent>,
	graphics: ComponentStore<import('pixi.js').Graphics>,
	platforms: ComponentStore<PlatformComponent>,
	ammos: ComponentStore<AmmoComponent>,
	options: EnemySystemOptions
): System {
	return ({ delta, world }) => {
		const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
		const timescale = world.getResource<TimeScaleResource>('timescale') ?? { value: 1 } as TimeScaleResource;
		const dt = delta * timescale.value;
		const stage = world.getResource<Application['stage']>('stage');
		const playerId = world.getResource<number>('player-entity');
		const camera = world.getResource<CameraResource>('camera');
		const bounds = world.getResource<StageBounds>(options.boundsKey);
		if (!stage || !playerId) return;
		const playerTransform = transforms.get(playerId);
		if (!playerTransform || !camera || !bounds) return;

		const dbg = world.getResource<DebugControlsResource>(options.debugControlsKey);
		if (dbg?.enemiesFrozen) {
			// Hard-freeze enemies: stop their velocity and skip AI+shooting.
			enemies.forEach((_enemy, entity) => {
				const v = velocities.get(entity);
				if (!v) return;
				v.x = 0;
				v.y = 0;
			});
			return;
		}

		enemies.forEach((enemy, entity) => {
			const transform = transforms.get(entity);
			if (!transform) return;
			const ammo = ammos.get(entity);
			if (!ammo) {
				ammos.set(entity, {
					capacity: options.ammoClipSize,
					remaining: options.ammoClipSize,
					reloadDuration: Math.max(0.05, tuning.enemyReloadDuration),
					reloadRemaining: 0,
					isEnemy: true
				});
			}

			// initialize legacy enemies (hot reload / saved state)
			if (!Number.isFinite(enemy.baseY)) enemy.baseY = transform.y;
			if (!Number.isFinite(enemy.vx)) enemy.vx = 0;
			if (!Number.isFinite(enemy.vy)) enemy.vy = 0;
			if (!Number.isFinite(enemy.repath)) enemy.repath = 0;
			if (!Number.isFinite(enemy.wanderPhase)) enemy.wanderPhase = Math.random() * Math.PI * 2;
			if (typeof enemy.hadLos !== 'boolean') enemy.hadLos = true;
			if (!Number.isFinite(enemy.burstShotsLeft)) enemy.burstShotsLeft = 0;
			if (!Number.isFinite(enemy.burstTimer)) enemy.burstTimer = 0;

			// --- movement AI ---
			if (tuning.enemyMoveEnabled) {
				enemy.wanderPhase += dt * tuning.enemyWanderFreq * 2 * Math.PI;

				// Recompute a desired X that restores line-of-sight to player
				enemy.repath -= dt;
				if (enemy.repath <= 0) {
					enemy.repath = tuning.enemyLosRepathInterval;
					if (tuning.enemyLosEnabled) {
						const hasLosNow = hasLineOfSight(
							transform.x,
							transform.y,
							playerTransform.x,
							playerTransform.y,
							platforms,
							tuning.enemyLosMargin
						);
						if (!hasLosNow) {
							const desired = findLineOfSightX(
								transform.x,
								transform.y,
								playerTransform.x,
								playerTransform.y,
								platforms,
								bounds,
								tuning
							);
							enemy.desiredX = desired;
						} else {
							enemy.desiredX = Number.NaN;
						}

						// If LOS was regained (i.e. "optimal trajectory") start a burst
						if (!enemy.hadLos && hasLosNow && tuning.enemyBurstEnabled) {
							const inView =
								!tuning.enemiesOnlyShootInView ||
								(transform.x >= 0 &&
									transform.x <= bounds.width &&
									transform.y >= camera.y &&
									transform.y <= camera.y + bounds.height);
							if (inView) {
								const minShots = Math.max(1, Math.round(tuning.enemyBurstCountMin));
								const maxShots = Math.max(minShots, Math.round(tuning.enemyBurstCountMax));
								enemy.burstShotsLeft = Math.floor(randomInRange(minShots, maxShots + 1));
								enemy.burstTimer = 0; // shoot immediately
								enemy.cooldown = 9999; // paused until burst finishes
							}
						}
						enemy.hadLos = hasLosNow;
					}
				}

				// Dodge nearby player projectiles (predict closest approach)
				let dodgeX = 0;
				let dodgeY = 0;
				if (tuning.enemyDodgeEnabled) {
					const enemyR = tuning.enemySize * 0.5;
					projectiles.forEach((p, pEntity) => {
						if (p.fromEnemy) return;
						const pT = transforms.get(pEntity);
						const pV = velocities.get(pEntity);
						if (!pT || !pV) return;
						const vv = pV.x * pV.x + pV.y * pV.y;
						if (vv <= 1e-6) return;
						const relX = pT.x - transform.x;
						const relY = pT.y - transform.y;
						const t = clamp(-(relX * pV.x + relY * pV.y) / vv, 0, tuning.enemyDodgeLookahead);
						const closestX = pT.x + pV.x * t;
						const closestY = pT.y + pV.y * t;
						const awayX = transform.x - closestX;
						const awayY = transform.y - closestY;
						const dist2 = awayX * awayX + awayY * awayY;
						const threatR = enemyR + p.size * 0.5 + tuning.enemyDodgePadding;
						if (dist2 <= threatR * threatR) {
							const len = Math.hypot(awayX, awayY) || 1;
							dodgeX += (awayX / len) * tuning.enemyDodgeStrength;
							dodgeY += (awayY / len) * tuning.enemyDodgeStrength;
						}
					});
				}

				// Wander adds "not straight line" even with LOS target
				const wander = Math.sin(enemy.wanderPhase) * tuning.enemyWanderStrength;
				let targetDirX = dodgeX + wander;

				if (Number.isFinite(enemy.desiredX)) {
					const dx = enemy.desiredX - transform.x;
					targetDirX += clamp(dx / Math.max(1, tuning.enemyLosSearchStep), -1, 1);
				}

				const dirLen = Math.hypot(targetDirX, dodgeY) || 1;
				const ndx = targetDirX / dirLen;
				const ndy = dodgeY / dirLen;
				const targetVx = ndx * tuning.enemyMoveSpeed;
				const targetVy = ndy * tuning.enemyMoveSpeed;

				enemy.vx = moveTowards(enemy.vx, targetVx, tuning.enemyMoveAccel * dt);
				enemy.vy = moveTowards(enemy.vy, targetVy, tuning.enemyMoveAccel * dt);

				transform.x += enemy.vx * dt;
				transform.y += enemy.vy * dt;

				// Clamp inside screen
				transform.x = clamp(transform.x, tuning.enemyMarginX, bounds.width - tuning.enemyMarginX);

				// Keep roughly on its spawn lane (prevents drifting forever)
				transform.y = lerp(transform.y, enemy.baseY, clamp(dt * 6, 0, 1));
			}

			const inView =
				!tuning.enemiesOnlyShootInView ||
				(transform.x >= 0 &&
					transform.x <= bounds.width &&
					transform.y >= camera.y &&
					transform.y <= camera.y + bounds.height);
			if (!inView) return;

			const enemyAmmo = ammos.get(entity);
			const isReloading = !!enemyAmmo && enemyAmmo.reloadRemaining > 0;
			const hasAmmo = !!enemyAmmo && enemyAmmo.remaining > 0;

			// Burst shooting (if active)
			if (enemy.burstShotsLeft > 0 && tuning.enemyBurstEnabled) {
				if (isReloading) return;
				if (enemyAmmo && !hasAmmo) {
					enemyAmmo.reloadRemaining = enemyAmmo.reloadDuration;
					return;
				}
				enemy.burstTimer -= dt;
				if (enemy.burstTimer > 0) return;

				// only burst if LOS is still valid; otherwise cancel
				const hasLosNow = !tuning.enemyLosEnabled
					? true
					: hasLineOfSight(
							transform.x,
							transform.y,
							playerTransform.x,
							playerTransform.y,
							platforms,
							tuning.enemyLosMargin
					  );
				if (!hasLosNow) {
					enemy.burstShotsLeft = 0;
					enemy.cooldown = randomInRange(tuning.enemyCooldownMin, tuning.enemyCooldownMax);
					return;
				}

				const dirX = playerTransform.x - transform.x;
				const dirY = playerTransform.y - transform.y;
				const baseAngle = Math.atan2(dirY, dirX);
				const spreadRad = (tuning.enemyBurstSpreadDeg * Math.PI) / 180;
				const angle = baseAngle + (Math.random() * 2 - 1) * spreadRad;
				const vx = Math.cos(angle) * tuning.projectileSpeed;
				const vy = Math.sin(angle) * tuning.projectileSpeed;
				const bulletSize = tuning.projectileSize;
				const spawnOffset = tuning.enemySize * 0.5 + bulletSize * 0.5 + 2;
				const spawnX = transform.x + Math.cos(angle) * spawnOffset;
				const spawnY = transform.y + Math.sin(angle) * spawnOffset;

				options.spawnProjectile(world, transforms, velocities, projectiles, graphics, stage, spawnX, spawnY, vx, vy, {
					fromEnemy: true,
					size: bulletSize,
					lifetime: tuning.projectileLifetime,
					fill: 0x991b1b,
					stroke: 0xfee2e2
				});
				if (enemyAmmo) {
					enemyAmmo.remaining = Math.max(0, enemyAmmo.remaining - 1);
					if (enemyAmmo.remaining <= 0) {
						enemyAmmo.reloadRemaining = enemyAmmo.reloadDuration;
					}
				}

				enemy.burstShotsLeft -= 1;
				if (enemy.burstShotsLeft <= 0) {
					enemy.cooldown = randomInRange(tuning.enemyCooldownMin, tuning.enemyCooldownMax);
					enemy.burstTimer = 0;
				} else {
					enemy.burstTimer = tuning.enemyBurstInterval;
				}
				return;
			}

			// Single shot (legacy behavior)
			if (isReloading) return;
			if (enemyAmmo && !hasAmmo) {
				enemyAmmo.reloadRemaining = enemyAmmo.reloadDuration;
				return;
			}
			enemy.cooldown -= dt;
			if (enemy.cooldown > 0) return;

			const dirX = playerTransform.x - transform.x;
			const dirY = playerTransform.y - transform.y;
			const len = Math.hypot(dirX, dirY) || 1;
			const vx = (dirX / len) * tuning.projectileSpeed;
			const vy = (dirY / len) * tuning.projectileSpeed;
			const bulletSize = tuning.projectileSize;
			const spawnOffset = tuning.enemySize * 0.5 + bulletSize * 0.5 + 2;
			const spawnX = transform.x + (dirX / len) * spawnOffset;
			const spawnY = transform.y + (dirY / len) * spawnOffset;

			options.spawnProjectile(world, transforms, velocities, projectiles, graphics, stage, spawnX, spawnY, vx, vy, {
				fromEnemy: true,
				size: bulletSize,
				lifetime: tuning.projectileLifetime,
				fill: 0x991b1b,
				stroke: 0xfee2e2
			});
			if (enemyAmmo) {
				enemyAmmo.remaining = Math.max(0, enemyAmmo.remaining - 1);
				if (enemyAmmo.remaining <= 0) {
					enemyAmmo.reloadRemaining = enemyAmmo.reloadDuration;
				}
			}
			enemy.cooldown = randomInRange(tuning.enemyCooldownMin, tuning.enemyCooldownMax);
		});
	};
}


